import {
  type ComponentEntity,
  type Entity,
  isComponentEntity,
} from '@backstage/catalog-model';
import { createFrontendModule } from '@backstage/frontend-plugin-api';
import {
  EntityCardBlueprint,
  EntityContentBlueprint,
} from '@backstage/plugin-catalog-react/alpha';
import { useEntity } from '@backstage/plugin-catalog-react';
import catalogPlugin from '@backstage/plugin-catalog/alpha';
import {
  isArgocdAvailable,
  EntityArgoCDHistoryCard,
  EntityArgoCDOverviewCard,
} from '@roadiehq/backstage-plugin-argo-cd';
import { DependencyCard } from '@internal/backstage-plugin-neo4j-ui-kit-react';
import { AboutCard } from '../components/catalog/AboutCard';
import { GroupProfileCard } from '../components/catalog/GroupProfileCard';
import { UserAchievementsCard } from '../components/catalog/UserAchievementsCard';
import { ApiSpecContent } from '../components/catalog/ApiSpecContent';

function isNonPluginComponent(entity: Entity): entity is ComponentEntity {
  return isComponentEntity(entity) && entity.spec.type !== 'plugin';
}

function DependencyCardWrapper() {
  const { entity } = useEntity();
  return <DependencyCard entity={entity} />;
}

function ArgocdContent() {
  return (
    <>
      <EntityArgoCDHistoryCard />
      <EntityArgoCDOverviewCard />
    </>
  );
}

export const catalogEntityModule = createFrontendModule({
  pluginId: catalogPlugin.pluginId,
  extensions: [
    // ── Entity page cards (→ entity page "cards" input) ─────
    // Custom About card for component/resource/location, including plugins
    // which otherwise only get this Overview card.
    // upstream About card handles api/system/domain/template via YAML filter in app-config.yaml
    EntityCardBlueprint.make({
      name: 'about-card',
      params: {
        filter: (entity: Entity) =>
          ['component', 'resource', 'location'].includes(
            entity.kind.toLowerCase(),
          ),
        loader: async () => <AboutCard variant="gridItem" />,
      },
    }),
    // api-docs replacements: upstream cards disabled in app-config.yaml,
    // replaced by these custom cards scoped to kind:api
    EntityCardBlueprint.make({
      name: 'api-relations-card',
      params: {
        filter: 'kind:api',
        loader: () =>
          import('@backstage/plugin-catalog-graph').then(m => (
            <m.EntityCatalogGraphCard height={400} />
          )),
      },
    }),
    EntityCardBlueprint.make({
      name: 'api-providers-card',
      params: {
        filter: 'kind:api',
        loader: () =>
          import('@backstage/plugin-api-docs').then(m => (
            <m.EntityProvidingComponentsCard />
          )),
      },
    }),
    EntityCardBlueprint.make({
      name: 'api-consumers-card',
      params: {
        filter: 'kind:api',
        loader: () =>
          import('@backstage/plugin-api-docs').then(m => (
            <m.EntityConsumingComponentsCard />
          )),
      },
    }),
    EntityCardBlueprint.make({
      name: 'dependency-card',
      params: {
        filter: isNonPluginComponent,
        loader: async () => <DependencyCardWrapper />,
      },
    }),
    // Replaces upstream entity-card:org/group-profile, disabled in app-config.yaml
    EntityCardBlueprint.make({
      name: 'group-profile-card',
      params: {
        filter: 'kind:group',
        type: 'info',
        loader: async () => <GroupProfileCard variant="gridItem" />,
      },
    }),
    EntityCardBlueprint.make({
      name: 'user-achievements-card',
      params: {
        filter: 'kind:user',
        loader: async () => <UserAchievementsCard />,
      },
    }),

    // ── Entity page tabs (→ entity page "contents" input) ───
    // Replaces upstream github-actions cards+tab, all disabled in app-config.yaml;
    // API provided by githubActionsModule.tsx
    EntityContentBlueprint.make({
      name: 'github-actions',
      params: {
        path: 'github-actions',
        title: 'GitHub Actions',
        filter: isNonPluginComponent,
        loader: () =>
          import('@internal/plugin-neo4j-github-actions').then(m => (
            <m.GithubActionsContent />
          )),
      },
    }),
    // Part of api-docs replacement; upstream content+cards disabled in app-config.yaml
    EntityContentBlueprint.make({
      name: 'api-spec',
      params: {
        path: 'api-spec',
        title: 'API Spec',
        filter: (entity: Entity) =>
          isNonPluginComponent(entity) && Boolean(entity.spec.providesApis),
        loader: async () => <ApiSpecContent />,
      },
    }),
    // Upstream ArgoCD ships 2 separate cards; both disabled in app-config.yaml.
    // We combine them into 1 content tab, reusing the upstream card components.
    EntityContentBlueprint.make({
      name: 'argocd',
      params: {
        path: 'argocd',
        title: 'ArgoCD',
        filter: (entity: Entity) =>
          isArgocdAvailable(entity) && isNonPluginComponent(entity),
        loader: async () => <ArgocdContent />,
      },
    }),
  ],
});
